class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    if (window.CartDrawerUpsells) window.CartDrawerUpsells.init();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

const CartDrawerUpsells = {
  async init() {
    const roots = document.querySelectorAll('[data-cart-upsell-root]');
    if (!roots.length) return;

    await Promise.all(
      Array.from(roots).map((root) => {
        return this.load(root);
      })
    );
  },

  async load(root) {
    const list = root.querySelector('[data-cart-upsell-list]');
    if (!list) return;

    const seedProductIds = this.parseIds(root.dataset.productIds);
    if (!seedProductIds.length) {
      list.innerHTML = '';
      root.hidden = true;
      return;
    }

    const cartTotal = Number(root.dataset.cartTotal || 0);
    const threshold = Number(root.dataset.threshold || 0);
    const currencyCode = root.dataset.currencyCode || 'GBP';
    const amountNeeded = Math.max(threshold - cartTotal, 0);

    list.innerHTML = '<p class="cart-drawer__recommendations-status">Loading recommendations...</p>';

    try {
      const products = await this.fetchRecommendations(seedProductIds);
      const rankedProducts = this.rankProducts({
        products,
        cartProductIds: seedProductIds,
        cartTotal,
        threshold,
        amountNeeded,
      });

      if (!rankedProducts.length) {
        list.innerHTML = '';
        root.hidden = true;
        return;
      }

      list.innerHTML = this.renderSlider(rankedProducts, currencyCode);
      root.hidden = false;
    } catch (error) {
      console.error('Cart recommendations error:', error);
      list.innerHTML = '';
      root.hidden = true;
    }
  },

  parseIds(value) {
    return (value || '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isFinite(id) && id > 0);
  },

  async fetchRecommendations(seedProductIds) {
    const uniqueSeedIds = [...new Set(seedProductIds)].slice(0, 4);
    const responses = await Promise.all(
      uniqueSeedIds.map((productId) =>
        fetch(`/recommendations/products.json?product_id=${productId}&limit=10&intent=related`).then((response) =>
          response.json()
        )
      )
    );

    const dedupedProducts = new Map();
    responses.forEach((payload) => {
      (payload.products || []).forEach((product) => {
        if (!dedupedProducts.has(product.id)) {
          dedupedProducts.set(product.id, product);
        }
      });
    });

    return Array.from(dedupedProducts.values());
  },

  rankProducts({ products, cartProductIds, threshold, amountNeeded }) {
    const cartProductSet = new Set(cartProductIds);

    return products
      .filter((product) => product && product.available && !cartProductSet.has(product.id))
      .map((product) => {
        const selectedVariant = this.getSelectedVariant(product);
        return selectedVariant ? { product, selectedVariant, price: Number(selectedVariant.price || 0) } : null;
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (amountNeeded <= 0 || threshold <= 0) {
          if (a.price !== b.price) return a.price - b.price;
          return a.product.title.localeCompare(b.product.title);
        }

        const aQualifies = a.price >= amountNeeded ? 0 : 1;
        const bQualifies = b.price >= amountNeeded ? 0 : 1;
        if (aQualifies !== bQualifies) return aQualifies - bQualifies;

        const aGap = Math.abs(a.price - amountNeeded);
        const bGap = Math.abs(b.price - amountNeeded);
        if (aGap !== bGap) return aGap - bGap;

        if (a.price !== b.price) return a.price - b.price;
        return a.product.title.localeCompare(b.product.title);
      })
      .slice(0, 2)
      .map(({ product }) => product);
  },

  getAvailableVariants(product) {
    return (product.variants || []).filter((variant) => variant.available);
  },

  getSelectedVariant(product) {
    const variants = this.getAvailableVariants(product);
    return variants[0] || (product.variants || [])[0] || null;
  },

  hasVariantChoices(product) {
    if (!product.variants || product.variants.length <= 1) return false;
    return product.variants.some((variant) => variant.title && variant.title !== 'Default Title');
  },

  formatMoney(cents, currencyCode) {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format((Number(cents) || 0) / 100);
  },

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  },

  handleize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  },

  getOptionName(product, position) {
    const option = (product.options || [])[position - 1];
    return typeof option === 'string' ? option : option?.name || '';
  },

  getOptionValues(product, position) {
    const values = [];
    (product.variants || []).forEach((variant) => {
      const value = variant[`option${position}`];
      if (value && !values.includes(value)) values.push(value);
    });
    return values;
  },

  getFirstMatchingVariant(product, position, value) {
    return (product.variants || []).find((variant) => variant[`option${position}`] === value) || null;
  },

  getSwatchValue(value) {
    const normalizedValue = String(value || '').toLowerCase();

    if (normalizedValue.includes('black')) return '#1a1a1a';
    if (normalizedValue.includes('white') || normalizedValue.includes('cream')) return '#f4f4ec';
    if (normalizedValue.includes('red') || normalizedValue.includes('burgundy') || normalizedValue.includes('merlot'))
      return '#8b1e2d';
    if (normalizedValue.includes('green') || normalizedValue.includes('forest') || normalizedValue.includes('sage'))
      return '#47564d';
    if (normalizedValue.includes('blue') || normalizedValue.includes('navy') || normalizedValue.includes('teal'))
      return '#4f6f8f';
    if (normalizedValue.includes('pink')) return '#d8a3b4';
    if (normalizedValue.includes('yellow')) return '#d8c15a';
    if (normalizedValue.includes('orange') || normalizedValue.includes('tan')) return '#b87a4b';
    if (normalizedValue.includes('purple') || normalizedValue.includes('lilac')) return '#7d6aa5';
    if (normalizedValue.includes('brown') || normalizedValue.includes('chocolate')) return '#6f4e37';
    if (normalizedValue.includes('grey') || normalizedValue.includes('gray')) return '#8d9396';

    return '#ccc';
  },

  renderPriceMarkup(price) {
    return `
      <div class="price">
        <div class="price__container">
          <div class="price__regular">
            <span class="price-item price-item--regular">${this.escapeHtml(price)}</span>
          </div>
        </div>
      </div>
    `;
  },

  renderOptionMarkup(product) {
    let markup = '';

    for (let position = 1; position <= 3; position += 1) {
      const optionName = this.getOptionName(product, position).toLowerCase();
      if (!optionName) continue;

      if (optionName.includes('color') || optionName.includes('colour')) {
        const values = this.getOptionValues(product, position);
        if (!values.length) continue;

        const swatches = values
          .map((value, index) => {
            const variant = this.getFirstMatchingVariant(product, position, value);
            const swatchValue = this.getSwatchValue(value);
            const variantImage = variant?.featured_image?.src || '';
            const stockClass = variant && variant.available ? '' : 'out-of-stock';

            return `<li
              class="option-values-option swatch ${index === 0 ? 'selected' : ''} ${stockClass}"
              data-value="${this.handleize(value)}"
              data-color-code="${this.escapeHtml(swatchValue)}"
              data-pro-id="${product.id}"
              style="background: ${this.escapeHtml(swatchValue)}; background-size: cover;"
              data-variant-id="${variant?.id || ''}"
              data-variant-image="${this.escapeHtml(variantImage)}"
            >${this.escapeHtml(value)}</li>`;
          })
          .join('');

        const currentSwatch = this.getSwatchValue(values[0]);

        markup += `
          <div class="swatch-list-wrapper">
            <ul class="color-swatch-list" id="color-swatch-${product.id}" style="display:none">
              ${swatches}
            </ul>
            <div class="selected-color" id="Colorv_${product.id}" data-proid="${product.id}">
              <span style="background: ${this.escapeHtml(currentSwatch)}; background-size: cover;"></span>
            </div>
          </div>
        `;
      } else if (optionName.includes('size')) {
        const values = this.getOptionValues(product, position);
        if (!values.length) continue;

        const options = values
          .map((value, index) => {
            const variant = this.getFirstMatchingVariant(product, position, value);
            return `<option
              class="option-values-option ${index === 0 ? 'selected' : ''}"
              data-value-size="${this.handleize(value)}"
              value="${this.escapeHtml(value)}"
              data-variant-id="${variant?.id || ''}"
            >${this.escapeHtml(value)}</option>`;
          })
          .join('');

        markup += `
          <select name="size-option-${product.id}" class="size-dropdown">
            ${options}
          </select>
        `;
      }
    }

    return markup;
  },

  renderVariantItemsMarkup(product, currencyCode) {
    return (product.variants || [])
      .map((variant) => {
        const quantity = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : variant.available ? 1 : 0;
        return `<li
          class="variant-item"
          data-variant-id="${variant.id}"
          data-variant-title="${this.escapeHtml(variant.title)}"
          data-option1="${this.escapeHtml(variant.option1 || '')}"
          data-option2="${this.escapeHtml(variant.option2 || '')}"
          data-option3="${this.escapeHtml(variant.option3 || '')}"
          data-id="${variant.id}"
          data-title="${this.handleize(variant.title)}"
          data-variant-image="${this.escapeHtml(variant.featured_image?.src || '')}"
          data-price="${this.escapeHtml(this.formatMoney(variant.price, currencyCode))}"
          data-quantity="${quantity}"
        >${this.escapeHtml(variant.title)}</li>`;
      })
      .join('');
  },

  renderSlider(products, currencyCode) {
    const sliderId = `Slider-CartUpsell-${products.map((product) => product.id).join('-')}`;

    const slides = products
      .map((product, index) => {
        return `
          <div
            id="Slide-${sliderId}-${index + 1}"
            class="complementary-slide complementary-slide--standard slider__slide slideshow__slide"
            tabindex="-1"
            role="group"
            aria-roledescription="slide"
            aria-label="${index + 1} of ${products.length}"
          >
            <ul class="list-unstyled" role="list">
              <li>${this.renderCard(product, currencyCode)}</li>
            </ul>
          </div>
        `;
      })
      .join('');

    const controls =
      products.length > 1
        ? `
          <div class="slider-buttons">
            <button
              type="button"
              class="slider-button slider-button--prev"
              name="previous"
              aria-label="Previous slide"
              aria-controls="${sliderId}"
            >
              <span class="svg-wrapper">
                <svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708 0l-4-4A.5.5 0 1 1 1.354.646L5 4.293 8.646.646a.5.5 0 0 1 .708 0Z" clip-rule="evenodd"/></svg>
              </span>
            </button>
            <div class="slider-counter slider-counter--dots">
              <div class="slideshow__control-wrapper">
                ${products
                  .map(
                    (_, index) => `
                      <button
                        class="slider-counter__link slider-counter__link--dots link${index === 0 ? ' slider-counter__link--active' : ''}"
                        aria-label="Load slide ${index + 1} of ${products.length}"
                        aria-controls="${sliderId}"
                        ${index === 0 ? 'aria-current="true"' : ''}
                      >
                        <span class="dot"></span>
                      </button>
                    `
                  )
                  .join('')}
              </div>
            </div>
            <button
              type="button"
              class="slider-button slider-button--next"
              name="next"
              aria-label="Next slide"
              aria-controls="${sliderId}"
            >
              <span class="svg-wrapper">
                <svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708 0l-4-4A.5.5 0 1 1 1.354.646L5 4.293 8.646.646a.5.5 0 0 1 .708 0Z" clip-rule="evenodd"/></svg>
              </span>
            </button>
          </div>
        `
        : '';

    return `
      <div class="complementary-products__container">
        <slideshow-component class="slider-component-full-width">
          <div
            id="${sliderId}"
            class="complementary-slider slider slider--everywhere"
            role="list"
            ${products.length > 1 ? 'aria-label="Slider"' : ''}
          >
            ${slides}
          </div>
          ${controls}
        </slideshow-component>
      </div>
    `;
  },

  renderCard(product, currencyCode) {
    const selectedVariant = this.getSelectedVariant(product);
    if (!selectedVariant) return '';

    const getImageSrc = (img) => {
      if (!img) return '';
      if (typeof img === 'string') return img;
      return img.src || '';
    };

    const image =
      getImageSrc(selectedVariant.featured_image) ||
      getImageSrc(product.featured_image);
      
    const price = this.formatMoney(selectedVariant.price, currencyCode);
    const variants = product.variants || [];
    const variantOptions = variants
      .map(
        (variant) =>
          `<option value="${variant.id}" data-name="${this.escapeHtml(variant.title)}">${this.escapeHtml(variant.title)}</option>`
      )
      .join('');
    const optionMarkup = this.renderOptionMarkup(product);
    const variantItemsMarkup = this.renderVariantItemsMarkup(product, currencyCode);

    return `
      <div data-cart-upsell-card>
        <div class="comp-card">
          <div class="comp-card__image">
            <a href="${this.escapeHtml(product.url)}">
              ${
                image
                  ? `<img
                      id="CompCardImage-${product.id}"
                      src="${this.escapeHtml(image)}"
                      alt="${this.escapeHtml(product.title)}"
                      loading="lazy"
                      width="400"
                      height="400"
                    >`
                  : ''
              }
            </a>
          </div>
          <div class="comp-card__info">
            <div class="comp-card__top">
              <a href="${this.escapeHtml(product.url)}" class="comp-card__title">${this.escapeHtml(product.title)}</a>
              <div class="comp-card__price" data-cart-upsell-price>${this.renderPriceMarkup(price)}</div>
            </div>
            <div class="comp-card__bottom">
              <select class="product_comp-variant" id="Cvariant-${product.id}" style="display:none">
                ${variantOptions}
              </select>
              ${optionMarkup}
              <ul class="product-variants" style="display:none;">
                ${variantItemsMarkup}
              </ul>
              <div class="Comp_add_to_cart">
                <button
                  id="Comp_add-${product.id}"
                  data-pid="${selectedVariant.id}"
                  data-variant-id="${selectedVariant.id}"
                  data-cart-upsell-add
                  class="Comp_add_btn"
                  ${selectedVariant.available ? '' : 'disabled'}
                >
                  ${selectedVariant.available ? 'Add to cart' : 'Sold out'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  },

  async addToCart(button) {
    const variantId = button.dataset.variantId;
    const cartDrawer = document.querySelector('cart-drawer');
    if (!variantId || !cartDrawer) return;

    button.setAttribute('aria-disabled', 'true');
    button.disabled = true;
    button.classList.add('loading');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append(
      'sections',
      cartDrawer.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    cartDrawer.setActiveElement(button);
    config.body = formData;

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const parsedResponse = await response.json();

      if (parsedResponse.status) {
        throw new Error(parsedResponse.description || 'Add to cart failed');
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-upsell',
        productVariantId: variantId,
        cartData: parsedResponse,
      });
      cartDrawer.renderContents(parsedResponse);
    } catch (error) {
      console.error('Cart upsell add error:', error);
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    } finally {
      button.classList.remove('loading');
    }
  },
};

window.CartDrawerUpsells = CartDrawerUpsells;

document.addEventListener('DOMContentLoaded', () => {
  CartDrawerUpsells.init();
});

document.addEventListener('change', (event) => {
  const select = event.target.closest('.cart-upsell-card__variant-select');
  if (!select) return;

  const card = select.closest('[data-cart-upsell-card]');
  const selectedOption = select.options[select.selectedIndex];
  if (!card || !selectedOption) return;

  const button = card.querySelector('[data-cart-upsell-add]');
  const price = card.querySelector('[data-cart-upsell-price]');
  if (button) {
    button.dataset.variantId = selectedOption.dataset.variantId || '';
    const isAvailable = selectedOption.dataset.available === 'true';
    button.disabled = !isAvailable;
    button.textContent = isAvailable ? 'Add to cart' : 'Sold out';
  }
  if (price && selectedOption.dataset.price) {
    price.textContent = selectedOption.dataset.price;
  }
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cart-upsell-add]');
  if (!button) return;

  event.preventDefault();
  if (button.classList.contains('loading')) return;
  CartDrawerUpsells.addToCart(button);
});


